import styled from "styled-components";
import { fontFamilies } from "components/shared/tokens/typography";
import { ChevronRight } from "lucide-react";

const COLORS = {
  page: "#f5f7fc",
  white: "#ffffff",
  title: "#293142",
  muted: "#7d8599",
  lightMuted: "#a7afbf",
  line: "#e1e6ef",
  brand: "#ef4f5f",
  status: "#155dcc",
  statusBackground: "#dce9ff",
  input: "#f2f4f8",
};

const MOBILE = "@media (max-width: 480px)";
const PAGE_MAX_WIDTH = "760px";

export const DetailPageShell = styled.main`
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: ${COLORS.page};
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
`;

export const DetailPageFrame = styled.div`
  display: flex;
  width: min(100%, ${PAGE_MAX_WIDTH});
  height: 100%;
  flex-direction: column;
  margin: 0 auto;
  background: ${COLORS.page};
  box-shadow: 0 0 30px rgba(41, 49, 66, 0.04);
`;

export const DetailHeader = styled.header`
  display: flex;
  min-height: 76px;
  flex: 0 0 auto;
  align-items: center;
  gap: 14px;
  padding: 12px 20px;
  background: ${COLORS.white};

  ${MOBILE} {
    min-height: 68px;
    padding: 10px 16px;
  }
`;

export const DetailHeaderCopy = styled.div`
  min-width: 0;

  h1 {
    overflow: hidden;
    margin: 0;
    color: ${COLORS.title};
    font-family: ${fontFamilies.bold};
    font-size: 16px;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    margin-top: 5px;
    color: ${COLORS.muted};
    font-size: 12px;
  }

  ${MOBILE} {
    h1 {
      font-size: 16px;
    }

    span {
      font-size: 11px;
    }
  }
`;

export const DetailStatus = styled.div`
  display: flex;
  min-height: 40px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 0 0 24px 24px;
  background: ${COLORS.statusBackground};
  color: ${COLORS.status};
  font-family: ${fontFamilies.semiBold};
  font-size: 14px;
  box-shadow: 0 3px 10px rgba(41, 49, 66, 0.04);

  ${MOBILE} {
    min-height: 36px;
    border-radius: 0 0 20px 20px;
    font-size: 13px;
  }
`;

export const DetailScrollArea = styled.div`
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

export const DetailContent = styled.div`
  padding: 30px 28px 40px;

  ${MOBILE} {
    padding: 22px 18px 32px;
  }
`;

export const GroupSummaryCard = styled.section`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  border-radius: 22px;
  padding: 24px 16px;
  background: ${COLORS.white};
  box-shadow: 0 4px 14px rgba(41, 49, 66, 0.09);

  ${MOBILE} {
    gap: 4px;
    border-radius: 18px;
    padding: 18px 7px;
  }
`;

export const SummaryItem = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

export const SummaryIcon = styled.span`
  display: inline-flex;
  margin-bottom: 8px;
  color: ${COLORS.muted};

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 1.9;
  }

  ${MOBILE} {
    margin-bottom: 6px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const SummaryLabel = styled.span`
  color: #929aab;
  font-size: 13px;
  line-height: 1.25;

  ${MOBILE} {
    font-size: 12px;
  }
`;

export const SummaryValue = styled.strong`
  max-width: 100%;
  overflow: hidden;
  margin-top: 5px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.semiBold};
  font-size: 15px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    font-size: 13px;
  }
`;

export const MapLink = styled.a`
  margin-top: 4px;
  border-bottom: 1px dotted ${COLORS.brand};
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;
  line-height: 1.25;
  text-decoration: none;

  ${MOBILE} {
    font-size: 12px;
  }
`;

export const ScheduledPOHeading = styled.h2`
  margin: 24px 0 16px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 18px;
  line-height: 1.2;

  ${MOBILE} {
    margin: 20px 0 14px;
    font-size: 16px;
  }
`;

export const PurchaseOrderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const PurchaseOrderCard = styled.article`
  overflow: hidden;
  border-radius: 22px;
  background: ${COLORS.white};
  box-shadow: 0 4px 14px rgba(41, 49, 66, 0.08);

  ${MOBILE} {
    border-radius: 18px;
  }
`;

export const PurchaseOrderTitle = styled.h3`
  margin: 0;
  padding: 20px 21px 14px;
  color: #3b455a;
  font-family: ${fontFamilies.bold};
  font-size: 16px;
  line-height: 1.25;
  overflow-wrap: anywhere;

  ${MOBILE} {
    padding: 17px 16px 13px;
    font-size: 14px;
  }
`;

export const PurchaseOrderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 17px;
  padding: 12px 21px 23px;

  ${MOBILE} {
    gap: 11px;
    padding: 10px 16px 20px;
  }
`;

export const RatioField = styled.div`
  position: relative;
  min-width: 0;
  border: 1px solid ${COLORS.line};
  border-radius: 14px;
  padding: 15px 12px 12px;
  background: ${COLORS.input};
`;

export const RatioLabel = styled.span`
  position: absolute;
  top: -9px;
  left: 11px;
  padding: 0 5px;
  background: ${COLORS.white};
  color: #a9b0bf;
  font-size: 12px;
  white-space: nowrap;

  ${MOBILE} {
    top: -8px;
    left: 8px;
    padding: 0 4px;
    font-size: 11px;
  }
`;

export const RatioValue = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 7px;
  color: ${COLORS.lightMuted};
  font-size: 13px;
  white-space: nowrap;

  strong {
    font-family: ${fontFamilies.semiBold};
    font-size: inherit;
    font-weight: normal;
  }

  span {
    overflow: hidden;
    font-size: inherit;
    text-overflow: ellipsis;
  }

  ${MOBILE} {
    font-size: 11px;
  }
`;

export const InvoiceSection = styled.section`
  padding: 2px 28px 30px;

  ${MOBILE} {
    padding: 2px 20px 26px;
  }
`;

export const InvoiceSectionTitle = styled.h4`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
  margin: 0 0 24px;
  color: #a3aabc;
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;
  line-height: 1.25;
  white-space: nowrap;

  &::after {
    height: 1px;
    min-width: 20px;
    flex: 1;
    background: ${COLORS.line};
    content: "";
  }

  ${MOBILE} {
    gap: 8px;
    margin-bottom: 21px;
    font-size: 13px;
  }
`;

export const InvoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;

  ${MOBILE} {
    gap: 22px;
  }
`;

export const InvoiceRow = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: 68px minmax(0, 1fr);
  align-items: center;
  gap: 18px;

  ${MOBILE} {
    grid-template-columns: 56px minmax(0, 1fr);
    gap: 14px;
  }
`;

export const SelectableInvoiceRow = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: 24px 68px minmax(0, 1fr);
  align-items: center;
  gap: 16px;

  ${MOBILE} {
    grid-template-columns: 20px 56px minmax(0, 1fr);
    gap: 12px;
  }
`;

export const InvoiceCheckbox = styled.input`
  position: relative;
  display: grid;
  width: 23px;
  height: 23px;
  appearance: none;
  place-content: center;
  border: 1.5px solid ${COLORS.brand};
  border-radius: 5px;
  margin: 0;
  background: ${COLORS.white};
  cursor: pointer;

  &::after {
    width: 10px;
    height: 6px;
    border-bottom: 2px solid ${COLORS.white};
    border-left: 2px solid ${COLORS.white};
    content: "";
    opacity: 0;
    transform: translateY(-1px) rotate(-45deg) scale(0.7);
    transition: opacity 120ms ease, transform 120ms ease;
  }

  &:checked {
    background: ${COLORS.brand};
  }

  &:checked::after {
    opacity: 1;
    transform: translateY(-1px) rotate(-45deg) scale(1);
  }

  &:focus-visible {
    outline: 3px solid rgba(239, 79, 95, 0.22);
    outline-offset: 2px;
  }

  ${MOBILE} {
    width: 20px;
    height: 20px;
    border-radius: 4px;

    &::after {
      width: 8px;
      height: 5px;
    }
  }
`;

export const DispatchedInvoiceRow = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: 68px minmax(0, 1fr) 24px;
  align-items: center;
  gap: 18px;

  ${MOBILE} {
    grid-template-columns: 56px minmax(0, 1fr) 20px;
    gap: 14px;
  }
`;

export const DispatchedInvoiceChevron = styled(ChevronRight)`
  width: 23px;
  height: 23px;
  color: ${COLORS.brand};
  stroke-width: 2;

  ${MOBILE} {
    width: 19px;
    height: 19px;
  }
`;

export const InvoiceThumbnail = styled.a`
  position: relative;
  display: block;
  width: 68px;
  height: 68px;
  overflow: hidden;
  border-radius: 9px;
  background: ${COLORS.input};
  color: ${COLORS.white};
  box-shadow: 0 3px 8px rgba(41, 49, 66, 0.14);

  > svg {
    position: absolute;
    right: 5px;
    bottom: 5px;
    width: 17px;
    height: 17px;
    padding: 2px;
    border-radius: 4px;
    background: rgba(41, 49, 66, 0.6);
    stroke-width: 1.9;
  }

  /* react-pdf canvas thumbnail */
  .react-pdf__Document,
  .react-pdf__Page,
  .react-pdf__Page__canvas {
    display: block;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
  }

  .react-pdf__Page__canvas {
    border-radius: 9px;
  }

  ${MOBILE} {
    width: 56px;
    height: 56px;
    border-radius: 8px;

    .react-pdf__Page__canvas {
      border-radius: 8px;
    }

    > svg {
      width: 15px;
      height: 15px;
    }
  }
`;

export const InvoiceThumbnailImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const InvoiceDocumentFallback = styled.span`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #7d8599;

  svg {
    width: 26px;
    height: 26px;
    stroke-width: 1.6;
  }
`;

export const InvoiceCopy = styled.div`
  min-width: 0;
`;

export const InvoiceNumber = styled.strong`
  display: block;
  overflow: hidden;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 15px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    font-size: 14px;
  }
`;

export const InvoiceMeta = styled.span`
  display: block;
  overflow: hidden;
  margin-top: 7px;
  color: ${COLORS.muted};
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    margin-top: 5px;
    font-size: 12px;
  }
`;

export const ShippingNoteNumber = styled.span`
  display: block;
  overflow: hidden;
  margin-top: 7px;
  color: #929aab;
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    margin-top: 5px;
    font-size: 12px;
  }
`;

export const InvoiceWeightWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 16px 12px;
  padding: 12px 14px;
  background: #fef0e8;
  border-radius: 12px;
  color: ${COLORS.title};
  font-size: 12px;
  line-height: 1.55;

  span {
    font-size: 12px;
    line-height: 1.55;
  }

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${COLORS.title};
  }
`;

export const InvoiceAction = styled.button`
  display: flex;
  width: 100%;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  padding: 0 18px;
  background: #fff4f5;
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 12px;
  cursor: pointer;

  svg {
    width: 15px;
    height: 15px;
  }

  &:active {
    transform: translateY(1px);
  }

  ${MOBILE} {
    min-height: 40px;
    font-size: 12px;
  }
`;

export const BottomActions = styled.footer`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 16px;
  border-top: 1px solid #edf0f5;
  padding: 22px 28px 28px;
  background: ${COLORS.white};
  box-shadow: 0 -8px 24px rgba(41, 49, 66, 0.05);

  ${MOBILE} {
    gap: 13px;
    padding: 17px 18px 20px;
  }
`;

export const EditScheduleButton = styled.button`
  min-height: 30px;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 15px;

  &:disabled {
    opacity: 1;
  }
`;

export const CreateShippingNoteButton = styled.button`
  min-height: 52px;
  border: 0;
  border-radius: 16px;
  padding: 0 20px;
  background: ${COLORS.brand};
  color: ${COLORS.white};
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;

  &:disabled {
    background: #cfd4de;
    color: ${COLORS.white};
  }

  ${MOBILE} {
    min-height: 48px;
    border-radius: 14px;
    font-size: 15px;
  }
`;


export const ModalIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #fef0e8;
  margin: 0 auto 14px;
`;
